import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../../../../useStore";
import { useAuthentication } from "../../../../Hooks/useAuthentication";
import CustomCalendar from "../../../Elements/Calendar/Calendar";
import { useToast } from "../../../../Contexts/Toast/toast.provider";
import { fmtCurrency } from "../../../../Utilities/Moneda/moneda";
import PagoSwitch from "../../../Elements/PagoSwitch/PagoSwitch";
import "./AdminWeeklySalesReport.css";

const toDateInput = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtRangoCorto = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

function buildWhatsAppUrl(phone, restaurantName, monto, rangoLabel) {
  if (!phone) return null;
  const clean = String(phone).replace(/[\s\-().]/g, "");
  const normalized = clean.startsWith("+") ? clean : `+52${clean}`;
  const message = `Hola ${restaurantName || ""}, te contactamos de BubUp: te corresponde ${fmtCurrency(monto)} por los pedidos pagados con Mercado Pago durante la semana del ${rangoLabel}. En breve te hacemos la transferencia. 🙌`;
  return `https://wa.me/${normalized.replace("+", "")}?text=${encodeURIComponent(message)}`;
}

export default function AdminWeeklySalesReport() {
  const navigate = useNavigate();
  const showToast = useToast();
  const { role } = useAuthentication();
  const isAdmin = ["admin", "administrador", "administrator"].includes(
    String(role || "").toLowerCase()
  );

  const {
    adminWeeklySalesReport,
    loadingAdminWeeklySalesReport,
    errorAdminWeeklySalesReport,
    fetchDataAdminWeeklySalesReport,
    setWeeklyPaymentStatus,
  } = useStore();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [q, setQ] = useState("");

  const refDate = useMemo(() => toDateInput(selectedDate), [selectedDate]);

  // Siempre apunta a la semana que está seleccionada AHORA MISMO — para que un
  // guardado que arrancó en una semana vieja no pise en pantalla una semana más
  // nueva a la que el admin ya cambió mientras esperaba la respuesta.
  const refDateRef = useRef(refDate);
  useEffect(() => {
    refDateRef.current = refDate;
  }, [refDate]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchDataAdminWeeklySalesReport(refDate);
  }, [isAdmin, refDate, fetchDataAdminWeeklySalesReport]);

  useEffect(() => {
    if (errorAdminWeeklySalesReport) showToast("error", errorAdminWeeklySalesReport);
  }, [errorAdminWeeklySalesReport, showToast]);

  const rango = adminWeeklySalesReport?.rango;
  const rangoLabel = useMemo(() => {
    if (!rango) return "";
    return `${fmtRangoCorto(rango.inicio)} – ${fmtRangoCorto(rango.fin)}`;
  }, [rango]);

  // La semana ya terminó → los números quedaron congelados, recién ahí es seguro
  // marcar el pago (si no, podría entrar más venta a esta misma semana después).
  const weekEnded = !!rango?.fin && new Date() >= new Date(rango.fin);

  const handleSavePago = async (restaurantId, { pagado, pagadoAt }) => {
    const res = await setWeeklyPaymentStatus({
      tipo: "negocio",
      entidadId: restaurantId,
      pagado,
      pagadoAt,
      date: refDate,
    });
    if (res?.statusCode === 200) {
      showToast("success", pagado ? "Pago marcado ✅" : "Pago desmarcado");
      // Refresca la semana que se está viendo AHORA (puede haber cambiado mientras
      // se guardaba), no la que estaba seleccionada cuando arrancó el guardado.
      fetchDataAdminWeeklySalesReport(refDateRef.current);
    } else {
      showToast("error", res?.message || "No se pudo actualizar el estado de pago");
    }
  };

  const restaurantesFiltrados = useMemo(() => {
    const list = adminWeeklySalesReport?.restaurantes || [];
    const query = q.trim().toLowerCase();
    if (!query) return list;
    return list.filter((r) => (r.restaurantName || "").toLowerCase().includes(query));
  }, [adminWeeklySalesReport, q]);

  if (!isAdmin) {
    return (
      <div className="container-fluid container-mh awsr-page">
        <div className="alert alert-danger">Esta sección es solo para administradores.</div>
      </div>
    );
  }

  const totales = adminWeeklySalesReport?.totalesGlobales;

  return (
    <div className="container-fluid container-mh awsr-page">
      <div className="row mb-3">
        <div className="col">
          <p className="awsr-subtext">
            Elige un día en el calendario para ver esa semana. Lo que se muestra en <strong>“Por pagar”</strong> es
            solo lo cobrado por Mercado Pago (eso lo tiene la plataforma). El efectivo lo cobra el <strong>repartidor</strong> directo
            del cliente — no el negocio — así que ese dinero se lo tiene que entregar el repartidor, no el admin.
            En ambos casos ya se descuenta el envío y la propina (son del repartidor).
            El switch "¿Ya le pagué?" solo se habilita cuando la semana ya terminó — así el número
            queda congelado y no puede marcarse pagado algo que después siga creciendo.
          </p>
          <div className="mb-3">
            <CustomCalendar value={selectedDate} onChange={setSelectedDate} />
          </div>
        </div>
      </div>

      {loadingAdminWeeklySalesReport && (
        <div className="text-center py-4 text-muted">Cargando reporte…</div>
      )}

      {!loadingAdminWeeklySalesReport && !!errorAdminWeeklySalesReport && (
        <div className="alert alert-danger">{errorAdminWeeklySalesReport}</div>
      )}

      {!loadingAdminWeeklySalesReport && !errorAdminWeeklySalesReport && adminWeeklySalesReport && (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <span className="awsr-rango">Semana: {rangoLabel}</span>
            <span className="awsr-count">
              {totales?.negociosConActividad || 0} negocio(s) con actividad · {totales?.pedidosTotalesEnRango || 0} pedido(s)
            </span>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-6 col-md-3">
              <div className="awsr-stat awsr-stat--total">
                <span className="awsr-stat-label">Total vendido</span>
                <span className="awsr-stat-value">{fmtCurrency(totales?.ventas?.total)}</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="awsr-stat awsr-stat--cash">
                <span className="awsr-stat-label">Efectivo</span>
                <span className="awsr-stat-value">{fmtCurrency(totales?.ventas?.efectivo)}</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="awsr-stat awsr-stat--pay">
                <span className="awsr-stat-label">Por pagar (MP)</span>
                <span className="awsr-stat-value">{fmtCurrency(totales?.porPagarTotal)}</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="awsr-stat awsr-stat--cancel">
                <span className="awsr-stat-label">Cancelado ({totales?.cancelados?.cantidad || 0})</span>
                <span className="awsr-stat-value">{fmtCurrency(totales?.cancelados?.monto)}</span>
              </div>
            </div>
          </div>

          <input
            type="text"
            className="form-control mb-3 awsr-search"
            placeholder="Buscar negocio…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          {restaurantesFiltrados.length === 0 ? (
            <div className="alert alert-warning text-center">⚠️ Ningún negocio tuvo pedidos esta semana.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle awsr-table">
                <thead>
                  <tr>
                    <th>Negocio</th>
                    <th>Pedidos</th>
                    <th>Efectivo</th>
                    <th>Mercado Pago</th>
                    <th>Por pagar</th>
                    <th>¿Ya le pagué?</th>
                    <th>Cancelados</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {restaurantesFiltrados.map((r) => {
                    const waUrl = buildWhatsAppUrl(r.phone, r.restaurantName, r.porPagar, rangoLabel);
                    return (
                      <tr key={r.restaurantId}>
                        <td>
                          <strong>{r.restaurantName || "Sin nombre"}</strong>
                          {r.phone && <div className="small text-muted">{r.phone}</div>}
                        </td>
                        <td>{r.pedidosTotalesEnRango}</td>
                        <td>{fmtCurrency(r.ventas?.efectivo)}</td>
                        <td>{fmtCurrency(r.ventas?.mercadoPago)}</td>
                        <td>
                          <strong className={r.porPagar > 0 ? "awsr-porpagar" : ""}>
                            {fmtCurrency(r.porPagar)}
                          </strong>
                        </td>
                        <td>
                          {r.porPagar > 0 ? (
                            <PagoSwitch
                              pagado={r.pagado}
                              pagadoAt={r.pagadoAt}
                              locked={!weekEnded}
                              onSave={(v) => handleSavePago(r.restaurantId, v)}
                            />
                          ) : (
                            <span className="text-muted small">— nada que pagar</span>
                          )}
                        </td>
                        <td>
                          {r.cancelados?.cantidad > 0
                            ? `${r.cancelados.cantidad} (${fmtCurrency(r.cancelados.monto)})`
                            : "—"}
                        </td>
                        <td className="text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            {waUrl && (
                              <a
                                href={waUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-success"
                              >
                                WhatsApp
                              </a>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => navigate(`/restaurants/${r.restaurantId}/orders`)}
                            >
                              Ver detalle
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
